<template>
    <div
        v-click-outside-app="closeAdditionalFilters"
        class="h-100"
    >
        <div
            @click="toggleAdditionalFilters"
            :class="{ 'transactions-filter__button--active': isAdditionalFiltersOpen }"
            class="transactions-filter__button transactions-filter__button--border-right transactions-filter__button--border-left"
        >
            <span class="material-icons">
                filter_alt
            </span>
            More filters
        </div>
        <transition name="fade">
            <div
                v-show="isAdditionalFiltersOpen"
                class="bg-white p-3 position-absolute transactions-filter-additional-dropdown"
            >
                <h5 class="mb-3">
                    Additional filters
                </h5>
                <hr>
                <div class="d-flex flex-wrap flex-md-nowrap">
                    <div class="transactions-filter-additional-dropdown__select-container">
                        <label>Websites</label>
                        <b-form-select
                            multiple
                            id="selectedNetwork"
                            @change="setWebsites"
                            :value="selected.websites"
                        >
                            <option
                                v-for="item in websites"
                                :key="item.id"
                                :value="item.id"
                            >
                                {{ item.domain }}
                            </option>
                        </b-form-select>
                    </div>
                    <div class="transactions-filter-additional-dropdown__select-container">
                        <label>Networks</label>
                        <b-form-select
                            multiple
                            id="selectedNetwork"
                            @change="setNetworks"
                            :value="selected.networks"
                        >
                            <option
                                v-for="network in networks"
                                :key="network.id"
                                :value="network.id"
                            >
                                {{ network.human_readable_name }}
                            </option>
                        </b-form-select>
                    </div>
                    <div class="transactions-filter-additional-dropdown__status-container">
                        <label>Status</label>
                        <d-checkbox
                            v-for="status in statuses"
                            :key="status.id"
                            name="status"
                            :value="status.id"
                            v-model="selected.statuses"
                            @change="setStatuses"
                        >
                            {{ status.label }}
                        </d-checkbox>
                    </div>
                    <div class="transactions-filter-additional-dropdown__status-container">
                        <label>Visitor profiles</label>
                        <d-checkbox
                            v-for="visitorProfile in visitorProfiles"
                            :key="visitorProfiles.indexOf(visitorProfile)"
                            name="visitorProfile"
                            :value="visitorProfile"
                            v-model="selected.visitorProfiles"
                            @change="setVisitorProfiles"
                        >
                            {{ visitorProfile }}
                        </d-checkbox>
                    </div>
                </div>
                <hr>
                <div class="d-flex flex-wrap justify-content-between justify-content-md-end align-items-center mt-2">
                    <div class="d-flex align-items-center mr-3 mb-3 mb-md-0">
                        <label for="disconnected" class="mb-0 p-0">
                            Show disconnected domains
                        </label>
                        <d-checkbox
                            id="disconnected"
                            inline
                            @change="setDisconnected"
                            :checked="selected.disconnected"
                            toggle-small
                            class="ml-2 mb-0 pr-1 d-block-inline"
                        />
                    </div>
                    <div>
                        <d-button
                            theme="secondary"
                            outline
                            class="mr-2 font-weight-bold"
                            @click="resetFilters"
                        >
                            Reset filters
                        </d-button>
                        <d-button
                            class="font-weight-bold"
                            @click="applyFilters"
                        >
                            Apply filters
                        </d-button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import TransactionDateFilter from './transactionDateFilter.vue'
import { AffiliateNetworkService, WebsiteService, VisitorProfilesService } from '@/main'
import Statuses from '@/models/transactions/statuses.model.js'
import outsideClickMixin from '@/mixins/outsideClick.js'
import notification from '@/mixins/notification'

export default {
    mixins: [outsideClickMixin, notification],

    components: {
        TransactionDateFilter
    },

    data() {
        return {
            selected: {
                websites: [],
                networks: [],
                statuses: [],
                visitorProfiles: [],
                disconnected: null
            },
            changed: {},
            isAdditionalFiltersOpen: false,

            statuses: Statuses,
            websites: [],
            networks: [],
            visitorProfiles: []
        }
    },

    methods: {
        toggleAdditionalFilters() {
            this.isAdditionalFiltersOpen = !this.isAdditionalFiltersOpen
        },
        closeAdditionalFilters() {
            this.isAdditionalFiltersOpen = false
        },
        applyFilters() {
            let parameters = {}

            if (this.changed.networks) {
                parameters.networks = this.selected.networks
            }

            if (this.changed.websites) {
                parameters.websites = this.selected.websites
            }

            if (this.changed.statuses) {
                parameters.statuses = this.selected.statuses
            }

            if (this.changed.visitorProfiles) {
                parameters.visitorProfiles = this.selected.visitorProfiles
            }

            if (this.changed.disconnected) {
                parameters.disconnected = this.selected.disconnected
            }

            this.$emit('applyAdditionalFilter', parameters)
            this.isAdditionalFiltersOpen = false
        },

        setWebsites(websites) {
            this.changed.websites = true
            this.selected.websites = websites
        },

        setNetworks(networks) {
            this.changed.networks = true
            this.selected.networks = networks
        },

        setStatuses(statuses) {
            this.changed.statuses = true
            this.selected.statuses.push(statuses)
        },

        setVisitorProfiles(visitorProfiles) {
            this.changed.visitorProfiles = true
            if (! this.selected.visitorProfiles) {
                this.selected.visitorProfiles.push(visitorProfiles)
            }
        },

        setDisconnected(value) {
            this.changed.disconnected = true
            this.selected.disconnected = value
        },

        async resetFilters() {
            await this.init()
            this.applyFilters()
        },

        async init() {
            this.setHandleForChangedParameters()
            await this.fillWebsites()
            await this.fillNetworks()
            this.fillStatuses()
            await this.fillVisitorProfiles()
            this.fillDisconnected()
        },

        setHandleForChangedParameters() {
            this.changed = {
                websites: false,
                networks: false,
                statuses: false,
                visitorProfiles: false,
                disconnected: false,
            }
        },

        async fillWebsites() {
            try {
                this.websites = await WebsiteService.get()

                this.selected.websites = this.websites.map(website => website.id)
            } catch(e) {
                this.showNotification('warn', 'Oops! we have a problem', 'Oops! cant get domains')
                throw e
            }
        },

        async fillNetworks() {
            try {
                let networks = await AffiliateNetworkService.getConnected()
                this.networks = networks.map((network) => {
                    return {
                        id: network.id,
                        human_readable_name: network.human_readable_name
                    }
                }).sort((a, b) => (a.human_readable_name > b.human_readable_name) ? 1 : -1)

                this.selected.networks = this.networks.map(network => network.id)
            } catch(e) {
                this.showNotification('warn', 'Oops! we have a problem', 'Cant get networks')
                throw e
            }
        },

        fillStatuses() {
            this.selected.statuses = Statuses.map(status => status.id)
        },

        async fillVisitorProfiles() {
            try {
                let { visitorProfiles } = await VisitorProfilesService.get()
                this.visitorProfiles = visitorProfiles.map(el => el.name)

                this.selected.visitorProfiles = this.visitorProfiles
            } catch(e) {
                this.showNotification('warn', 'Oops! we have a problem', 'Cant get visitor profiles')
                throw e
            }
        },

        fillDisconnected() {
            this.selected.disconnected = true
        },
    },
    async mounted() {
        await this.init()
    }
}
</script>
